/**
 * 聊天消息本地存储服务 - 使用IndexedDB
 * 支持离线环境下的消息读取和存储
 */

const DB_NAME = 'lingshu_chat_db';
const DB_VERSION = 1;
const STORE_MESSAGES = 'messages';
const STORE_CONVERSATIONS = 'conversations';
const STORE_USER_INFO = 'user_info';

export interface StoredMessage {
  id: string;
  userId: string; // 当前登录用户ID
  conversationId: string;
  chatType: 'singleChat' | 'groupChat';
  from: string;
  to: string;
  type: string;
  msg: string;
  time: number;
  [key: string]: any;
}

export interface StoredConversation {
  id: string; // userId_conversationId 复合主键
  userId: string;
  conversationId: string;
  chatType: 'singleChat' | 'groupChat';
  name?: string;
  lastMessage?: any;
  unreadCount?: number;
  timestamp?: number;
  [key: string]: any;
}

export interface StoredUserInfo {
  id: string; // userId
  data: Record<string, any>;
  timestamp: number;
}

class ChatStorageService {
  private currentUserId: string = '';
  private db: IDBDatabase | null = null;
  private dbPromise: Promise<IDBDatabase> | null = null;

  /**
   * 初始化数据库
   */
  private async initDB(): Promise<IDBDatabase> {
    if (this.db) return this.db;
    if (this.dbPromise) return this.dbPromise;

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        reject(new Error('打开IndexedDB失败'));
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // 创建消息表
        if (!db.objectStoreNames.contains(STORE_MESSAGES)) {
          const messageStore = db.createObjectStore(STORE_MESSAGES, { keyPath: 'id' });
          messageStore.createIndex('userId', 'userId', { unique: false });
          messageStore.createIndex('conversationId', 'conversationId', { unique: false });
          messageStore.createIndex('userId_conversationId', ['userId', 'conversationId'], { unique: false });
          messageStore.createIndex('time', 'time', { unique: false });
        }

        // 创建会话表
        if (!db.objectStoreNames.contains(STORE_CONVERSATIONS)) {
          const conversationStore = db.createObjectStore(STORE_CONVERSATIONS, { keyPath: 'id' });
          conversationStore.createIndex('userId', 'userId', { unique: false });
          conversationStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // 创建用户信息表
        if (!db.objectStoreNames.contains(STORE_USER_INFO)) {
          db.createObjectStore(STORE_USER_INFO, { keyPath: 'id' });
        }
      };
    });

    return this.dbPromise;
  }

  /**
   * 设置当前用户ID
   */
  setCurrentUser(userId: string) {
    this.currentUserId = userId;
  }

  /**
   * 保存消息到IndexedDB
   */
  async saveMessages(messages: Omit<StoredMessage, 'userId'>[], conversationId?: string): Promise<void> {
    if (!this.currentUserId) return;

    try {
      const db = await this.initDB();
      const transaction = db.transaction([STORE_MESSAGES], 'readwrite');
      const store = transaction.objectStore(STORE_MESSAGES);

      messages.forEach(msg => {
        store.put({
          ...msg,
          userId: this.currentUserId,
        } as StoredMessage);
      });

      await this.waitForTransaction(transaction);
      console.log(`已保存 ${messages.length} 条消息到IndexedDB`);
    } catch (error) {
      console.error('保存消息到IndexedDB失败:', error);
    }
  }

  /**
   * 从IndexedDB读取消息
   */
  async getMessages(conversationId?: string): Promise<StoredMessage[]> {
    if (!this.currentUserId) return [];

    try {
      const db = await this.initDB();
      const transaction = db.transaction([STORE_MESSAGES], 'readonly');
      const store = transaction.objectStore(STORE_MESSAGES);

      let messages: StoredMessage[];

      if (conversationId) {
        // 读取特定会话的消息
        const index = store.index('userId_conversationId');
        const request = index.getAll([this.currentUserId, conversationId]);
        messages = await this.waitForRequest<StoredMessage[]>(request);
      } else {
        // 读取当前用户的所有消息
        const index = store.index('userId');
        const request = index.getAll(this.currentUserId);
        messages = await this.waitForRequest<StoredMessage[]>(request);
      }

      // 按时间排序
      return messages.sort((a, b) => a.time - b.time);
    } catch (error) {
      console.error('读取消息失败:', error);
      return [];
    }
  }

  /**
   * 保存会话列表
   */
  async saveConversations(conversations: Omit<StoredConversation, 'id' | 'userId'>[]): Promise<void> {
    if (!this.currentUserId) return;

    try {
      const db = await this.initDB();
      const transaction = db.transaction([STORE_CONVERSATIONS], 'readwrite');
      const store = transaction.objectStore(STORE_CONVERSATIONS);

      conversations.forEach(cvs => {
        store.put({
          ...cvs,
          id: `${this.currentUserId}_${cvs.conversationId}`,
          userId: this.currentUserId,
          timestamp: Date.now(),
        } as StoredConversation);
      });

      await this.waitForTransaction(transaction);
      console.log(`已保存 ${conversations.length} 个会话到IndexedDB`);
    } catch (error) {
      // console.error('保存会话失败:', error);
    }
  }

  /**
   * 从IndexedDB读取会话列表
   */
  async getConversations(): Promise<StoredConversation[]> {
    if (!this.currentUserId) return [];

    try {
      const db = await this.initDB();
      const transaction = db.transaction([STORE_CONVERSATIONS], 'readonly');
      const store = transaction.objectStore(STORE_CONVERSATIONS);
      const index = store.index('userId');
      const request = index.getAll(this.currentUserId);

      const conversations = await this.waitForRequest<StoredConversation[]>(request);
      
      // 按时间戳排序
      return conversations.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
    } catch (error) {
      console.error('读取会话列表失败:', error);
      return [];
    }
  }

  /**
   * 保存用户信息
   */
  async saveUserInfo(userInfo: Record<string, any>): Promise<void> {
    if (!this.currentUserId) return;

    try {
      const db = await this.initDB();
      const transaction = db.transaction([STORE_USER_INFO], 'readwrite');
      const store = transaction.objectStore(STORE_USER_INFO);

      const data: StoredUserInfo = {
        id: this.currentUserId,
        data: userInfo,
        timestamp: Date.now(),
      };

      store.put(data);
      await this.waitForTransaction(transaction);
    } catch (error) {
      console.error('保存用户信息失败:', error);
    }
  }

  /**
   * 获取用户信息
   */
  async getUserInfo(userId?: string): Promise<Record<string, any>> {
    const uid = userId || this.currentUserId;
    if (!uid) return {};

    try {
      const db = await this.initDB();
      const transaction = db.transaction([STORE_USER_INFO], 'readonly');
      const store = transaction.objectStore(STORE_USER_INFO);
      const request = store.get(uid);

      const result = await this.waitForRequest<StoredUserInfo | undefined>(request);
      return result?.data || {};
    } catch (error) {
      console.error('读取用户信息失败:', error);
      return {};
    }
  }

  /**
   * 清除当前用户的所有数据
   */
  async clearUserData(): Promise<void> {
    if (!this.currentUserId) return;

    try {
      const db = await this.initDB();

      // 清除消息
      const msgTx = db.transaction([STORE_MESSAGES], 'readwrite');
      const msgStore = msgTx.objectStore(STORE_MESSAGES);
      const msgIndex = msgStore.index('userId');
      const msgRequest = msgIndex.openCursor(IDBKeyRange.only(this.currentUserId));

      msgRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };

      await this.waitForTransaction(msgTx);

      // 清除会话
      const cvsTx = db.transaction([STORE_CONVERSATIONS], 'readwrite');
      const cvsStore = cvsTx.objectStore(STORE_CONVERSATIONS);
      const cvsIndex = cvsStore.index('userId');
      const cvsRequest = cvsIndex.openCursor(IDBKeyRange.only(this.currentUserId));

      cvsRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };

      await this.waitForTransaction(cvsTx);

      // 清除用户信息
      const userTx = db.transaction([STORE_USER_INFO], 'readwrite');
      const userStore = userTx.objectStore(STORE_USER_INFO);
      userStore.delete(this.currentUserId);
      await this.waitForTransaction(userTx);

      console.log('已清除当前用户的所有本地数据');
    } catch (error) {
      console.error('清除数据失败:', error);
    }
  }

  /**
   * 删除特定会话的消息
   */
  async deleteConversationMessages(conversationId: string): Promise<void> {
    if (!this.currentUserId) return;

    try {
      const db = await this.initDB();
      const transaction = db.transaction([STORE_MESSAGES], 'readwrite');
      const store = transaction.objectStore(STORE_MESSAGES);
      const index = store.index('userId_conversationId');
      const request = index.openCursor(IDBKeyRange.only([this.currentUserId, conversationId]));

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };

      await this.waitForTransaction(transaction);
      console.log(`已删除会话 ${conversationId} 的所有消息`);
    } catch (error) {
      console.error('删除会话消息失败:', error);
    }
  }

  /**
   * 获取数据库统计信息
   */
  async getStats(): Promise<{ messageCount: number; conversationCount: number }> {
    if (!this.currentUserId) return { messageCount: 0, conversationCount: 0 };

    try {
      const db = await this.initDB();

      // 统计消息数量
      const msgTx = db.transaction([STORE_MESSAGES], 'readonly');
      const msgStore = msgTx.objectStore(STORE_MESSAGES);
      const msgIndex = msgStore.index('userId');
      const msgCountRequest = msgIndex.count(this.currentUserId);
      const messageCount = await this.waitForRequest<number>(msgCountRequest);

      // 统计会话数量
      const cvsTx = db.transaction([STORE_CONVERSATIONS], 'readonly');
      const cvsStore = cvsTx.objectStore(STORE_CONVERSATIONS);
      const cvsIndex = cvsStore.index('userId');
      const cvsCountRequest = cvsIndex.count(this.currentUserId);
      const conversationCount = await this.waitForRequest<number>(cvsCountRequest);

      return { messageCount, conversationCount };
    } catch (error) {
      console.error('获取统计信息失败:', error);
      return { messageCount: 0, conversationCount: 0 };
    }
  }

  // 工具方法：等待IDBRequest完成
  private waitForRequest<T>(request: IDBRequest): Promise<T> {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // 工具方法：等待IDBTransaction完成
  private waitForTransaction(transaction: IDBTransaction): Promise<void> {
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(new Error('Transaction aborted'));
    });
  }
}

export const chatStorage = new ChatStorageService();
